// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp, updateDoc, arrayUnion } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const register = async (email, password, userData = {}) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create Firestore user document with additional user data
      const userDocData = {
        email: userCredential.user.email,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        phone: userData.phone || '',
        createdAt: serverTimestamp(),
        roles: ['user'],
        location: null,
        postalCode: '',
        mode: null,
      };

      // Add organization data if provided (user was invited)
      if (userData.organizationId) {
        userDocData.organizationId = userData.organizationId;
        userDocData.organizationName = userData.organizationName || '';
        userDocData.organizationRole = 'member'; // Default role for invited users
        
        // Add user to organization's members array
        try {
          const orgRef = doc(db, 'organizations', userData.organizationId);
          const orgDoc = await getDoc(orgRef);
          
          if (orgDoc.exists()) {
            await updateDoc(orgRef, {
              members: arrayUnion({
                userId: userCredential.user.uid,
                name: userData.firstName && userData.lastName 
                  ? `${userData.firstName} ${userData.lastName}` 
                  : undefined,
                email: userCredential.user.email,
                phone: userData.phone || undefined,
              }),
              updatedAt: new Date(),
            });
          }
        } catch (orgErr) {
          console.error('Error adding user to organization members:', orgErr);
          // Continue anyway - user is created, just not added to org members
        }
      }

      await setDoc(doc(db, 'users', userCredential.user.uid), userDocData);
      
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error('Registration error:', error);
      let errorMessage = 'Failed to create account';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Email is already in use';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Check if user document exists in Firestore
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      const userDoc = await getDoc(userDocRef);
      
      // Create document if it doesn't exist (for existing users)
      if (!userDoc.exists()) {
        await setDoc(userDocRef, {
          email: userCredential.user.email,
          createdAt: serverTimestamp(),
          roles: ['user'],
          location: null,
          postalCode: '',
          mode: null,
        });
      }
      
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = 'Failed to sign in';
      
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      
      // Check if user document exists
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      const userDoc = await getDoc(userDocRef);
      
      // Create document if it doesn't exist
      if (!userDoc.exists()) {
        const displayName = userCredential.user.displayName || '';
        const nameParts = displayName.split(' ');
        
        await setDoc(userDocRef, {
          email: userCredential.user.email,
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          phone: userCredential.user.phoneNumber || '',
          createdAt: serverTimestamp(),
          roles: ['user'],
          location: null,
          postalCode: '',
          mode: null,
        });
      }
      
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error('Google login error:', error);
      let errorMessage = 'Failed to sign in with Google';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign in cancelled';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup was blocked by browser';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => signOut(auth);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      register, 
      login, 
      loginWithGoogle,
      logout 
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};